/** FINANCE VAULT — dialogs: upload (drag/drop), bulk upload, bank account,
 *  secure preview, details + versions, pack builder. */
import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText } from "lucide-react";
import { Modal, Field } from "../operations/StockInventoryDialogs";

const inputCls = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";
const ENTITIES = [["SUNTRONIX_CORPORATE", "SUNTRONIX CORPORATE"], ["PERSONAL", "PERSONAL"]];
const MONTHS = ["", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC", "JAN", "FEB", "MAR"];
const SENS = ["", "CORPORATE_CONFIDENTIAL", "FINANCE_RESTRICTED", "OWNER_ONLY", "PERSONAL_PRIVATE"];
const CA_TAGS = ["", "REQUESTED_BY_CA", "SHARED_WITH_CA", "RECEIVED_FROM_CA", "FINAL"];

function entityOptions(access) {
  return ENTITIES.filter(([v]) =>
    (v === "SUNTRONIX_CORPORATE" && access.corporate) || (v === "PERSONAL" && access.personal));
}

// ----------------------------------------------------------------- UPLOAD
export function UploadDialog({ access, preset, onClose, onDone }) {
  const opts = entityOptions(access);
  const [f, setF] = useState({ entity: preset.entity || opts[0]?.[0] || "",
    doc_type: preset.doc_type || "", title: preset.title || "",
    financial_year: preset.financial_year || "", assessment_year: "",
    institution: preset.institution || "", bank_account_id: preset.bank_account_id || "",
    account_reference: "", month: preset.month || "", document_date: "",
    valid_from: "", valid_until: "", sensitivity: "",
    version: preset.version || "1.0",
    notes: "", ca_tag: "", ca_name: "", ca_date: "", ca_reference: "",
    supersedes_id: preset.supersedes_id || "" });
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [banks, setBanks] = useState([]);
  const inputRef = useRef(null);
  useEffect(() => {
    api.get("/finance-vault/bank-accounts").then(({ data }) => setBanks(data.items || []));
  }, []);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    if (!f.entity || !f.doc_type || !f.title) { toast.error("Entity, type and title required"); return; }
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => { if (v) fd.append(k, v); });
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/finance-vault/documents", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(f.supersedes_id ? "New version filed — previous marked SUPERSEDED"
                                    : "Financial document filed");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };

  return (
    <Modal title={f.supersedes_id ? "Upload new version" : "Add Financial Document"} onClose={onClose} wide>
      <div data-testid="fv-upload-dialog">
        <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false);
              if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]); }}
            onClick={() => inputRef.current?.click()}
            data-testid="fv-dropzone"
            className={"border-2 border-dashed rounded-xl p-6 text-center cursor-pointer mb-4 " +
              (drag ? "border-amber-400 bg-amber-50" : "border-slate-300 hover:bg-slate-50")}>
          <UploadCloud className="h-6 w-6 mx-auto text-slate-400 mb-1" />
          {file ? <div className="text-[11.5px] font-medium">{file.name}
              <span className="text-slate-400"> · {(file.size / 1024).toFixed(0)} KB</span></div>
            : <div className="text-[11px] text-slate-500">Drag & drop or click to choose (PDF, images, Office, ZIP · max 40 MB)</div>}
          <input ref={inputRef} type="file" className="hidden" data-testid="fv-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Entity" required>
            <select value={f.entity} onChange={set("entity")} data-testid="fv-up-entity" className={inputCls}
                disabled={!!preset.entity}>
              {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></Field>
          <Field label="Document type" required>
            <select value={f.doc_type} onChange={set("doc_type")} data-testid="fv-up-doctype" className={inputCls}>
              <option value="">— pick —</option>
              {access.doc_types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select></Field>
          <Field label="Title" required>
            <input value={f.title} onChange={set("title")} data-testid="fv-up-title" className={inputCls} /></Field>
          <Field label="Financial year">
            <select value={f.financial_year} onChange={set("financial_year")} data-testid="fv-up-fy" className={inputCls}>
              <option value="">—</option>
              {access.fy_options.map((y) => <option key={y} value={y}>{y}</option>)}
            </select></Field>
          <Field label="Assessment year">
            <input value={f.assessment_year} onChange={set("assessment_year")} placeholder="AY 2026-27"
                data-testid="fv-up-ay" className={inputCls} /></Field>
          <Field label="Month (statements)">
            <select value={f.month} onChange={set("month")} data-testid="fv-up-month" className={inputCls}>
              {MONTHS.map((m) => <option key={m} value={m}>{m || "—"}</option>)}
            </select></Field>
          <Field label="Bank account">
            <select value={f.bank_account_id} onChange={set("bank_account_id")}
                data-testid="fv-up-bank" className={inputCls}>
              <option value="">—</option>
              {banks.filter((b) => b.entity === f.entity || b.id === f.bank_account_id).map((b) =>
                <option key={b.id} value={b.id}>{b.bank_name} {b.account_masked}</option>)}
            </select></Field>
          <Field label="Bank / Institution">
            <input value={f.institution} onChange={set("institution")} data-testid="fv-up-institution"
                className={inputCls} /></Field>
          <Field label="Account reference">
            <input value={f.account_reference} onChange={set("account_reference")} className={inputCls} /></Field>
          <Field label="Document date">
            <input type="date" value={f.document_date} onChange={set("document_date")} className={inputCls} /></Field>
          <Field label="Valid from">
            <input type="date" value={f.valid_from} onChange={set("valid_from")} className={inputCls} /></Field>
          <Field label="Valid until">
            <input type="date" value={f.valid_until} onChange={set("valid_until")} className={inputCls} /></Field>
          <Field label="Sensitivity (default by entity)">
            <select value={f.sensitivity} onChange={set("sensitivity")} data-testid="fv-up-sensitivity" className={inputCls}>
              {SENS.map((s) => <option key={s} value={s}>{s ? s.replace(/_/g, " ") : "AUTO"}</option>)}
            </select></Field>
          <Field label="Version">
            <input value={f.version} onChange={set("version")} className={inputCls} /></Field>
          <Field label="CA tag">
            <select value={f.ca_tag} onChange={set("ca_tag")} data-testid="fv-up-catag" className={inputCls}>
              {CA_TAGS.map((t) => <option key={t} value={t}>{t ? t.replace(/_/g, " ") : "—"}</option>)}
            </select></Field>
          {f.ca_tag && <>
            <Field label="CA / Auditor name">
              <input value={f.ca_name} onChange={set("ca_name")} className={inputCls} /></Field>
            <Field label="CA date">
              <input type="date" value={f.ca_date} onChange={set("ca_date")} className={inputCls} /></Field>
            <Field label="CA reference">
              <input value={f.ca_reference} onChange={set("ca_reference")} className={inputCls} /></Field>
          </>}
          <div className="col-span-3">
            <Field label="Notes">
              <input value={f.notes} onChange={set("notes")} className={inputCls} /></Field>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="fv-up-submit"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Filing…" : "File document"}</button>
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------ BULK UPLOAD
export function BulkUploadDialog({ access, fy, onClose, onDone }) {
  const opts = entityOptions(access);
  const [entity, setEntity] = useState(opts[0]?.[0] || "");
  const [docType, setDocType] = useState("OTHER_FINANCIAL_DOCUMENT");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const submit = async () => {
    if (!files.length) { toast.error("Choose files"); return; }
    const fd = new FormData();
    fd.append("entity", entity); fd.append("doc_type", docType);
    fd.append("financial_year", fy);
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data } = await api.post("/finance-vault/documents/bulk", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.documents.length} document(s) filed as REVIEW REQUIRED — classify them from the list`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk upload failed"); }
    setBusy(false);
  };
  return (
    <Modal title="Bulk upload (files land as REVIEW REQUIRED)" onClose={onClose}>
      <div className="space-y-3" data-testid="fv-bulk-dialog">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Entity" required>
            <select value={entity} onChange={(e) => setEntity(e.target.value)}
                data-testid="fv-bulk-entity" className={inputCls}>
              {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></Field>
          <Field label="Default document type">
            <select value={docType} onChange={(e) => setDocType(e.target.value)}
                data-testid="fv-bulk-doctype" className={inputCls}>
              {access.doc_types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select></Field>
        </div>
        <div onClick={() => ref.current?.click()} data-testid="fv-bulk-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault();
              setFiles([...files, ...Array.from(e.dataTransfer.files || [])]); }}
            className="border-2 border-dashed border-slate-300 rounded-xl p-5 text-center cursor-pointer hover:bg-slate-50">
          <UploadCloud className="h-5 w-5 mx-auto text-slate-400 mb-1" />
          <div className="text-[11px] text-slate-500">
            {files.length ? `${files.length} file(s) selected` : "Drop multiple files or click (max 25)"}</div>
          <input ref={ref} type="file" multiple className="hidden" data-testid="fv-bulk-input"
              onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])} />
        </div>
        {files.length > 0 && <div className="max-h-28 overflow-y-auto text-[10.5px] space-y-0.5">
          {files.map((fl, i) => <div key={i} className="flex items-center gap-1 text-slate-600">
            <FileText className="h-3 w-3" />{fl.name}</div>)}</div>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="fv-bulk-submit"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Uploading…" : "Upload for review"}</button>
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------ BANK ACCOUNT
export function BankAccountDialog({ access, onClose, onDone }) {
  const opts = entityOptions(access);
  const [f, setF] = useState({ entity: opts[0]?.[0] || "", bank_name: "",
    account_type: "CURRENT", account_name: "", account_number: "", branch: "",
    ifsc: "", currency: "INR" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.bank_name || !f.account_name) { toast.error("Bank and account name required"); return; }
    try {
      await api.post("/finance-vault/bank-accounts", f);
      toast.success("Bank account added (number stored masked only)"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title="Add bank account" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="fv-bank-dialog">
        <Field label="Entity" required>
          <select value={f.entity} onChange={set("entity")} data-testid="fv-ba-entity" className={inputCls}>
            {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></Field>
        <Field label="Bank name" required>
          <input value={f.bank_name} onChange={set("bank_name")} data-testid="fv-ba-bank" className={inputCls} /></Field>
        <Field label="Account type">
          <select value={f.account_type} onChange={set("account_type")} className={inputCls}>
            {["CURRENT", "SAVINGS", "OD", "CC", "LOAN", "FD", "OTHER"].map((t) =>
              <option key={t}>{t}</option>)}
          </select></Field>
        <Field label="Account name" required>
          <input value={f.account_name} onChange={set("account_name")} data-testid="fv-ba-name" className={inputCls} /></Field>
        <Field label="Account number (only last 4 kept, masked)">
          <input value={f.account_number} onChange={set("account_number")} data-testid="fv-ba-number"
              className={inputCls} placeholder="e.g. 50100234564321" /></Field>
        <Field label="Branch">
          <input value={f.branch} onChange={set("branch")} className={inputCls} /></Field>
        <Field label="IFSC">
          <input value={f.ifsc} onChange={set("ifsc")} className={inputCls} /></Field>
        <Field label="Currency">
          <input value={f.currency} onChange={set("currency")} className={inputCls} /></Field>
      </div>
      <div className="text-[9.5px] text-slate-400 mt-2">
        Never store NetBanking passwords, OTPs, PINs or CVVs here — the vault will not accept credentials.</div>
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={submit} data-testid="fv-ba-submit"
            className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white">Add account</button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- PREVIEW
export function PreviewDialog({ doc, onClose }) {
  const url = `${API}/finance-vault/documents/${doc.id}/preview`;
  const isImg = /^image\//.test(doc.file_type || "");
  const isPdf = (doc.file_type || "").includes("pdf");
  return (
    <Modal title={`Preview — ${doc.title}`} onClose={onClose} wide>
      <div data-testid="fv-preview-dialog" className="min-h-[420px]">
        {isImg && <img src={url} alt={doc.title} className="max-h-[70vh] mx-auto rounded" />}
        {isPdf && <iframe title="preview" src={url} className="w-full h-[70vh] rounded border border-slate-200" />}
        {!isImg && !isPdf && (
          <div className="text-center text-[11.5px] text-slate-500 py-16">
            No inline preview for {doc.file_ext} — use Download for secure access.</div>)}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------- DETAILS + VERSIONS
export function DetailsDialog({ doc, access, openUpload, onClose, onDone }) {
  const [f, setF] = useState({ title: doc.title, doc_type: doc.doc_type,
    financial_year: doc.financial_year || "", assessment_year: doc.assessment_year || "",
    institution: doc.institution || "", month: doc.month || "",
    status: doc.status, notes: doc.notes || "", ca_tag: doc.ca_tag || "",
    ca_name: doc.ca_name || "", ca_reference: doc.ca_reference || "" });
  const [versions, setVersions] = useState([]);
  useEffect(() => {
    api.get(`/finance-vault/documents/${doc.id}/versions`)
      .then(({ data }) => setVersions(data.versions || []));
  }, [doc.id]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      await api.patch(`/finance-vault/documents/${doc.id}`,
        Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "")));
      toast.success("Metadata updated"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title={`Document details — ${doc.title}`} onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-3" data-testid="fv-details-dialog">
        <Field label="Title"><input value={f.title} onChange={set("title")}
            data-testid="fv-det-title" className={inputCls} /></Field>
        <Field label="Type">
          <select value={f.doc_type} onChange={set("doc_type")} className={inputCls}>
            {access.doc_types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Status">
          <select value={f.status} onChange={set("status")} data-testid="fv-det-status" className={inputCls}>
            {["CURRENT", "REVIEW_REQUIRED", "SUPERSEDED", "INACTIVE"].map((s) =>
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Financial year">
          <select value={f.financial_year} onChange={set("financial_year")} className={inputCls}>
            <option value="">—</option>
            {access.fy_options.map((y) => <option key={y} value={y}>{y}</option>)}
          </select></Field>
        <Field label="Assessment year">
          <input value={f.assessment_year} onChange={set("assessment_year")} className={inputCls} /></Field>
        <Field label="Month">
          <select value={f.month} onChange={set("month")} className={inputCls}>
            {MONTHS.map((m) => <option key={m} value={m}>{m || "—"}</option>)}
          </select></Field>
        <Field label="Institution">
          <input value={f.institution} onChange={set("institution")} className={inputCls} /></Field>
        <Field label="CA tag">
          <select value={f.ca_tag} onChange={set("ca_tag")} className={inputCls}>
            {CA_TAGS.map((t) => <option key={t} value={t}>{t ? t.replace(/_/g, " ") : "—"}</option>)}
          </select></Field>
        <Field label="CA name">
          <input value={f.ca_name} onChange={set("ca_name")} className={inputCls} /></Field>
        <div className="col-span-3">
          <Field label="Notes"><input value={f.notes} onChange={set("notes")} className={inputCls} /></Field>
        </div>
      </div>
      <div className="mt-4">
        <div className="text-[10.5px] font-semibold mb-1">Version history</div>
        <div className="space-y-1">
          {versions.map((v) => (
            <div key={v.id} className={"flex items-center gap-2 text-[10.5px] border rounded px-2 py-1.5 " +
                (v.id === doc.id ? "border-slate-900" : "border-slate-200")}>
              <span className="font-mono">v{v.version}</span>
              <span className="flex-1">{v.filename}</span>
              <span className="text-slate-400">{(v.uploaded_on || "").slice(0, 10)} · {v.uploaded_by}</span>
              <span className={"text-[8.5px] font-semibold px-1.5 py-0.5 rounded " +
                (v.status === "CURRENT" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600")}>
                {v.status}</span>
            </div>))}
        </div>
      </div>
      <div className="flex justify-between gap-2 mt-4">
        <button onClick={() => { onClose();
            openUpload({ entity: doc.entity, doc_type: doc.doc_type,
              financial_year: doc.financial_year, month: doc.month,
              bank_account_id: doc.bank_account_id, institution: doc.institution,
              title: doc.title, supersedes_id: doc.id,
              version: `${(parseFloat(doc.version) || 1) + 1}.0`.replace(/\.0\.0$/, ".0") }); }}
            data-testid="fv-det-new-version"
            className="text-[11px] px-3 py-1.5 rounded-md border border-amber-400 text-amber-800">
          Upload new version</button>
        <div className="space-x-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Close</button>
          <button onClick={save} data-testid="fv-det-save"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white">Save metadata</button>
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------- PACK
export function PackDialog({ access, fy, onClose, onDone }) {
  const opts = entityOptions(access);
  const [entity, setEntity] = useState(opts[0]?.[0] || "");
  const [year, setYear] = useState(fy);
  const [title, setTitle] = useState("");
  const [docs, setDocs] = useState([]);
  const [sel, setSel] = useState([]);
  useEffect(() => {
    api.get("/finance-vault/documents",
      { params: { entity, financial_year: year, status: "CURRENT" } })
      .then(({ data }) => { setDocs(data.items || []); setSel([]); });
  }, [entity, year]);
  useEffect(() => {
    setTitle(`${entity === "PERSONAL" ? "Personal Tax Pack" : "Suntronix Financial Pack"} ${year}`);
  }, [entity, year]);
  const submit = async () => {
    if (!sel.length) { toast.error("Select documents"); return; }
    try {
      await api.post("/finance-vault/packs",
        { entity, financial_year: year, title, doc_ids: sel });
      toast.success("Pack generated — originals unchanged"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title="Create Financial Pack" onClose={onClose} wide>
      <div className="space-y-3" data-testid="fv-pack-dialog">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Entity" required>
            <select value={entity} onChange={(e) => setEntity(e.target.value)}
                data-testid="fv-pk-entity" className={inputCls}>
              {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></Field>
          <Field label="Financial year" required>
            <select value={year} onChange={(e) => setYear(e.target.value)}
                data-testid="fv-pk-fy" className={inputCls}>
              {access.fy_options.map((y) => <option key={y} value={y}>{y}</option>)}
            </select></Field>
          <Field label="Pack title" required>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
                data-testid="fv-pk-title" className={inputCls} /></Field>
        </div>
        <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
          {docs.length === 0 && <div className="text-center text-slate-400 text-[11px] py-6">
            No CURRENT documents for {year}.</div>}
          {docs.map((d) => (
            <label key={d.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 text-[11px] cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={sel.includes(d.id)}
                  data-testid={`fv-pk-doc-${d.id}`}
                  onChange={() => setSel((s) => s.includes(d.id)
                    ? s.filter((x) => x !== d.id) : [...s, d.id])} />
              <span className="flex-1">{d.title}</span>
              <span className="text-[9.5px] text-slate-400">{d.doc_type.replace(/_/g, " ")}</span>
            </label>))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} data-testid="fv-pk-submit"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white">
            Generate pack ({sel.length})</button>
        </div>
      </div>
    </Modal>
  );
}
